import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { grcService } from '../services/grcService';
import { IngestionProgress } from '../types/grc';

interface IngestionJob {
  logId: number;
  frameworkId: number;
  fileName: string;
  progress: IngestionProgress;
}

interface IngestionProgressContextType {
  activeJobs: IngestionJob[];
  startTracking: (logId: number, frameworkId: number, fileName: string) => void;
  dismissJob: (logId: number) => void;
  dismissAll: () => void;
  hideWidget: () => void;
  showWidget: () => void;
  isWidgetVisible: boolean;
}

const IngestionProgressContext = createContext<IngestionProgressContextType>({
  activeJobs: [],
  startTracking: () => {},
  dismissJob: () => {},
  dismissAll: () => {},
  hideWidget: () => {},
  showWidget: () => {},
  isWidgetVisible: true,
});

export const useIngestionProgress = () => useContext(IngestionProgressContext);

const STORAGE_KEY = 'imperion_active_ingestions';

// Step labels in Serbian
const stepLabels: Record<string, string> = {
  extracting_text: 'Ekstrakcija teksta',
  detecting_type: 'Detekcija tipa',
  preprocessing: 'Predprocesiranje',
  chunking: 'Segmentacija',
  ai_extraction: 'AI ekstrakcija',
  post_validation: 'Post-validacija',
  building_hierarchy: 'Kreiranje hijerarhije',
  creating_sections: 'Finalizacija',
  completed: 'Završeno',
  failed: 'Greška',
};

export const IngestionProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState<IngestionJob[]>([]);
  const [isWidgetVisible, setIsWidgetVisible] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Restore tracked jobs from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: { logId: number; frameworkId: number; fileName: string }[] = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setActiveJobs(parsed.map(j => ({
            ...j,
            progress: {
              id: j.logId, framework_id: j.frameworkId, source_file: j.fileName,
              total_chunks: 0, processed_chunks: 0, current_step: null,
              progress_percent: 0, step_detail: 'Učitavanje statusa...', controls_found: 0,
              pages_processed: 0, status: 'processing', error_message: null,
              started_at: null, completed_at: null,
            }
          })));
        }
      }
    } catch {}
  }, []);

  // Persist tracked job IDs to localStorage
  useEffect(() => {
    const toStore = activeJobs
      .filter(j => j.progress.status === 'processing')
      .map(j => ({ logId: j.logId, frameworkId: j.frameworkId, fileName: j.fileName }));
    if (toStore.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [activeJobs]);

  // Poll active jobs every 3 seconds
  const pollProgress = useCallback(async () => {
    setActiveJobs(prev => {
      const processingJobs = prev.filter(j => j.progress.status === 'processing');
      if (processingJobs.length === 0) return prev;

      // Fire off polls for each job
      Promise.all(
        processingJobs.map(j => grcService.getIngestionProgress({ logId: j.logId }))
      ).then(results => {
        setActiveJobs(current => 
          current.map(job => {
            const idx = processingJobs.findIndex(p => p.logId === job.logId);
            if (idx >= 0 && results[idx]?.[0]) {
              return { ...job, progress: results[idx][0] };
            }
            return job;
          })
        );
      }).catch(() => {});

      return prev; // Return unchanged until promises resolve
    });
  }, []);

  useEffect(() => {
    const hasActive = activeJobs.some(j => j.progress.status === 'processing');
    if (hasActive && !intervalRef.current) {
      intervalRef.current = setInterval(pollProgress, 3000);
      pollProgress(); // Immediate first poll
    } else if (!hasActive && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeJobs, pollProgress]);

  const startTracking = useCallback((logId: number, frameworkId: number, fileName: string) => {
    setActiveJobs(prev => {
      if (prev.some(j => j.logId === logId)) return prev;
      return [...prev, {
        logId, frameworkId, fileName,
        progress: {
          id: logId, framework_id: frameworkId, source_file: fileName,
          total_chunks: 0, processed_chunks: 0, current_step: 'extracting_text',
          progress_percent: 2, step_detail: 'Započeta obrada...', controls_found: 0,
          pages_processed: 0, status: 'processing', error_message: null,
          started_at: new Date().toISOString(), completed_at: null,
        }
      }];
    });
  }, []);

  const dismissJob = useCallback((logId: number) => {
    setActiveJobs(prev => prev.filter(j => j.logId !== logId));
  }, []);

  const dismissAll = useCallback(() => {
    setActiveJobs([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const hideWidget = useCallback(() => {
    setIsWidgetVisible(false);
  }, []);

  const showWidget = useCallback(() => {
    setIsWidgetVisible(true);
  }, []);

  // Auto-dismiss completed jobs after 10 seconds
  useEffect(() => {
    const completedJobs = activeJobs.filter(j => j.progress.status === 'completed');
    if (completedJobs.length > 0) {
      const timer = setTimeout(() => {
        setActiveJobs(prev => prev.filter(j => j.progress.status === 'processing' || j.progress.status === 'failed'));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [activeJobs]);

  return (
    <IngestionProgressContext.Provider value={{ activeJobs, startTracking, dismissJob, dismissAll, hideWidget, showWidget, isWidgetVisible }}>
      {children}
      {/* Floating progress panel */}
      {activeJobs.length > 0 && isWidgetVisible && <FloatingProgressPanel jobs={activeJobs} onDismiss={dismissJob} onDismissAll={dismissAll} onHide={hideWidget} />}
      {/* Show minimized indicator when widget is hidden but jobs exist */}
      {activeJobs.length > 0 && !isWidgetVisible && (
        <button
          onClick={showWidget}
          className="fixed bottom-4 right-4 z-[9999] bg-indigo-600 text-white px-3 py-2 rounded-full shadow-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
          title="Prikaži aktivne zadatke"
        >
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-sm font-medium">{activeJobs.filter(j => j.progress.status === 'processing').length} aktivno</span>
        </button>
      )}
    </IngestionProgressContext.Provider>
  );
};

// ─── Floating Progress Panel (bottom-right, persists across pages) ───
const FloatingProgressPanel: React.FC<{ 
  jobs: IngestionJob[]; 
  onDismiss: (id: number) => void;
  onDismissAll: () => void;
  onHide: () => void;
}> = ({ jobs, onDismiss, onDismissAll, onHide }) => {
  const [collapsed, setCollapsed] = useState(false);
  const activeCount = jobs.filter(j => j.progress.status === 'processing').length;
  const hasCompleted = jobs.some(j => j.progress.status === 'completed' || j.progress.status === 'failed');

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-96 max-w-[calc(100vw-2rem)]">
      {/* Header */}
      <div className="flex items-center bg-indigo-600 text-white px-3 py-2 rounded-t-xl shadow-lg">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex-1 flex items-center gap-2 font-medium text-sm text-left hover:opacity-90"
        >
          {activeCount > 0 && (
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          )}
          Obrada dokumenata {activeCount > 0 ? `(${activeCount} aktivno)` : ''}
          <span className="ml-auto text-lg">{collapsed ? '▲' : '▼'}</span>
        </button>
        
        {/* Clear All / Hide buttons */}
        <div className="flex items-center gap-1 ml-2 border-l border-indigo-400 pl-2">
          {hasCompleted && (
            <button
              onClick={onDismissAll}
              className="p-1.5 hover:bg-indigo-700 rounded text-xs"
              title="Obriši sve"
            >
              🗑️
            </button>
          )}
          <button
            onClick={onHide}
            className="p-1.5 hover:bg-indigo-700 rounded text-xs"
            title="Sakrij panel"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Jobs list */}
      {!collapsed && (
        <div className="bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl shadow-2xl max-h-80 overflow-y-auto">
          {jobs.map(job => (
            <JobProgressItem key={job.logId} job={job} onDismiss={onDismiss} />
          ))}
        </div>
      )}
    </div>
  );
};

const JobProgressItem: React.FC<{ job: IngestionJob; onDismiss: (id: number) => void }> = ({ job, onDismiss }) => {
  const { progress } = job;
  const percent = Number(progress.progress_percent) || 0;
  const isComplete = progress.status === 'completed';
  const isFailed = progress.status === 'failed';
  const isDone = isComplete || isFailed;
  const isStale = !isDone && percent === 0 && progress.step_detail?.includes('Učitavanje');
  const stepLabel = stepLabels[progress.current_step || ''] || progress.current_step || 'Priprema...';

  return (
    <div className={`p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${isFailed ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[70%]" title={job.fileName}>
          {job.fileName}
        </span>
        {/* Allow dismissing any job (especially stale "restoring" ones) */}
        <button
          onClick={() => onDismiss(job.logId)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs px-1"
          title={isDone ? 'Ukloni' : isStale ? 'Ukloni zaostalu stavku' : 'Poništi praćenje'}
        >
          ✕
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-1">
        <div
          className={`h-full transition-all duration-700 ease-out rounded-full ${
            isFailed ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-indigo-500'
          }`}
          style={{ width: `${isComplete ? 100 : percent}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <span className={`${isFailed ? 'text-red-600 dark:text-red-400' : isComplete ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {isFailed ? `Greška: ${progress.error_message?.substring(0, 60) || 'Nepoznata'}` :
           isComplete ? `Završeno: ${progress.controls_found} kontrola` :
           (progress.step_detail || stepLabel)}
        </span>
        <span className={`font-bold tabular-nums ${isFailed ? 'text-red-600' : isComplete ? 'text-green-600' : 'text-indigo-600 dark:text-indigo-400'}`}>
          {isComplete ? '100' : Math.round(percent)}%
        </span>
      </div>

      {/* Chunk detail for AI extraction */}
      {!isDone && progress.total_chunks > 0 && progress.current_step === 'ai_extraction' && (
        <div className="text-[10px] text-gray-400 mt-0.5">
          Segment {progress.processed_chunks}/{progress.total_chunks} • {progress.controls_found} kontrola pronađeno
        </div>
      )}
    </div>
  );
};
